import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useApi, useMutation } from '../../lib/useApi.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import { api } from '../../lib/apiClient.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  BrandedEmptyState,
  DateInput,
  ErrorState,
  FormField,
  InlineScoreCell,
  KpiBandSkeleton,
  KpiHeroBand,
  LoadingSkeleton,
  PageActionStrip,
  PageHeader,
  Select,
  StatusDonutMini,
  StaggerReveal,
  TextInput,
  toast,
} from '../../components/index.js';
import { usePeople } from '../../lib/usePeople.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { MeetingTimeConversionPanel } from '../time/MeetingTimeConversionPanel.jsx';
import './actions.css';
import './meetings.redesign.css';

// --- helpers -------------------------------------------------------------

function startOfWeek(now) {
  const d = new Date(now);
  const day = d.getDay();
  const diff = (day + 6) % 7; // Monday-based week start
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - diff);
  return d;
}

function classifyMeetings(meetings, now = Date.now()) {
  const weekStart = startOfWeek(now);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  let upcoming = 0;
  let thisWeek = 0;
  let past = 0;
  let actionItems = 0;
  let undated = 0;
  for (const m of meetings) {
    actionItems += Array.isArray(m.actionIds) ? m.actionIds.length : 0;
    const ts = m.startAt ? Date.parse(m.startAt) : NaN;
    if (Number.isNaN(ts)) {
      undated += 1;
      continue;
    }
    if (ts >= now) upcoming += 1;
    else past += 1;
    if (ts >= weekStart.getTime() && ts < weekEnd.getTime()) thisWeek += 1;
  }
  return { upcoming, thisWeek, past, actionItems, undated };
}

// Roster timeframe filter (AC-MTG-1.1). Undated meetings have not been held,
// so they surface under "Upcoming" rather than disappearing.
function matchesTimeframe(meeting, timeframe, now) {
  if (timeframe === 'all') return true;
  const ts = meeting.startAt ? Date.parse(meeting.startAt) : NaN;
  if (Number.isNaN(ts)) return timeframe === 'upcoming';
  return timeframe === 'upcoming' ? ts >= now : ts < now;
}

function meetingTone(meeting, now = Date.now()) {
  const ts = meeting.startAt ? Date.parse(meeting.startAt) : NaN;
  if (Number.isNaN(ts)) return 'slate';
  const dayMs = 86_400_000;
  if (ts < now) return 'slate';
  if (ts - now <= 2 * dayMs) return 'amber'; // imminent
  return 'blue';
}

function formatStart(startAt) {
  if (!startAt) return 'TBD';
  return startAt.slice(0, 16).replace('T', ' ');
}

// Initials for the roster avatar. Prefers the resolved display name ("Morgan Hayes" -> "MH");
// falls back to the trailing token of a raw id ("USR-PJM-TURNER" -> "TU").
function initialsFor(nameOrId) {
  if (!nameOrId) return '··';
  const parts = String(nameOrId)
    .split(/[-_\s]/)
    .filter(Boolean);
  if (parts.length >= 2 && !/^USR$/i.test(parts[0])) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  const last = parts[parts.length - 1] || nameOrId;
  return last.slice(0, 2).toUpperCase();
}

const TIMEFRAME_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
];

// --- list page -----------------------------------------------------------

export function MeetingManagerPage() {
  const res = useApi('/meetings', { query: { limit: 100 } });
  const createMut = useMutation();
  const { people, nameOf } = usePeople();

  const [timeframe, setTimeframe] = useState('all');
  const [creating, setCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createStartDate, setCreateStartDate] = useState('');
  const [attendeeToAdd, setAttendeeToAdd] = useState('');
  const [createAttendees, setCreateAttendees] = useState([]);

  usePageMetadata({
    title: 'Meeting Manager',
    subtitle:
      'Meeting records, notes, decisions, and draft-action extraction across OWHA delivery workflows.',
  });

  const meetings = useMemo(() => res.data?.data || [], [res.data]);
  const stats = useMemo(() => classifyMeetings(meetings), [meetings]);

  if (res.loading) {
    return (
      <section className="owha-grid meetings-redesign">
        <PageHeader
          eyebrow="Collaboration"
          title="Meeting Manager"
          subtitle="Track key meetings and convert meeting notes into draft actions under human review."
        />
        <KpiBandSkeleton count={3} />
        <LoadingSkeleton rows={6} />
      </section>
    );
  }
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;

  const now = Date.now();
  const filteredMeetings = meetings.filter((m) => matchesTimeframe(m, timeframe, now));
  const peopleOptions = people.map((person) => ({ value: person.id, label: person.displayName }));

  function resetCreateForm() {
    setCreateTitle('');
    setCreateStartDate('');
    setAttendeeToAdd('');
    setCreateAttendees([]);
  }

  function addAttendee() {
    if (!attendeeToAdd) return;
    setCreateAttendees((current) =>
      current.includes(attendeeToAdd) ? current : [...current, attendeeToAdd],
    );
    setAttendeeToAdd('');
  }

  function removeAttendee(userId) {
    setCreateAttendees((current) => current.filter((id) => id !== userId));
  }

  async function createMeeting() {
    const title = createTitle.trim();
    if (!title) {
      toast.error({ title: 'Meeting title is required' });
      return;
    }
    if (!createStartDate) {
      toast.error({ title: 'Start date is required' });
      return;
    }
    try {
      const response = await createMut.run(() =>
        api.post('/meetings', {
          title,
          startAt: `${createStartDate}T09:00:00.000Z`,
          attendees: createAttendees,
        }),
      );
      toast.success({
        title: 'Meeting scheduled',
        body: `${response?.data?.title || title} is on the calendar.`,
      });
      setCreating(false);
      resetCreateForm();
      res.refresh();
    } catch (error) {
      toast.error({ title: 'Could not schedule meeting', body: error.message });
    }
  }

  const kpiItems = [
    {
      id: 'upcoming',
      label: 'Upcoming meetings',
      value: stats.upcoming,
      icon: '📅',
      status: stats.upcoming > 0 ? 'blue' : 'slate',
      statusLabel: stats.upcoming > 0 ? 'Scheduled' : 'None',
      footer: `${stats.past} held`,
    },
    {
      id: 'this-week',
      label: 'This week',
      value: stats.thisWeek,
      icon: '🗓️',
      status: stats.thisWeek > 0 ? 'amber' : 'slate',
      statusLabel: stats.thisWeek > 0 ? 'On the calendar' : 'Clear',
      footer: 'Monday-based week',
    },
    {
      id: 'action-items',
      label: 'Action items',
      value: stats.actionItems,
      icon: '✅',
      status: stats.actionItems > 0 ? 'sage' : 'slate',
      statusLabel: stats.actionItems > 0 ? 'Governed' : 'None yet',
      footer: 'Converted from meetings',
    },
  ];

  const donutSegments = [
    { label: 'Upcoming', count: stats.upcoming, tone: 'blue' },
    { label: 'This week', count: stats.thisWeek, tone: 'amber' },
    { label: 'Held', count: stats.past, tone: 'slate' },
  ];

  const createPanel = (
    <article className="card meeting-create-card">
      <div className="eyebrow">Schedule</div>
      <h2 className="card-title">New meeting</h2>
      <p className="muted meeting-create-card__hint">
        Give the meeting a title and start date, then build the attendee roster from the people
        directory. Attendance drives time-draft conversion later.
      </p>
      <div className="meeting-create-grid">
        <FormField label="Meeting title" htmlFor="mtg-create-title">
          <TextInput
            id="mtg-create-title"
            value={createTitle}
            onChange={setCreateTitle}
            placeholder="e.g. Gate 3 readiness review"
            disabled={createMut.running}
          />
        </FormField>
        <FormField label="Start date" htmlFor="mtg-create-start">
          <DateInput
            id="mtg-create-start"
            value={createStartDate}
            onChange={setCreateStartDate}
            disabled={createMut.running}
          />
        </FormField>
        <FormField
          label="Attendee"
          htmlFor="mtg-create-attendee"
          hint="Pick a person, then add them to the roster."
        >
          <div className="meeting-create-attendee-row">
            <Select
              id="mtg-create-attendee"
              value={attendeeToAdd}
              onChange={setAttendeeToAdd}
              placeholder="Select a person…"
              options={peopleOptions}
              disabled={createMut.running}
            />
            <button
              type="button"
              className="btn outline"
              onClick={addAttendee}
              disabled={!attendeeToAdd || createMut.running}
            >
              Add attendee
            </button>
          </div>
        </FormField>
      </div>
      {createAttendees.length ? (
        <ul className="meeting-roster meeting-create-roster">
          {createAttendees.map((userId) => (
            <li key={userId}>
              <span className="meeting-roster__avatar" aria-hidden="true">
                {initialsFor(nameOf(userId))}
              </span>
              <span className="meeting-roster__name">
                <PersonLabel userId={userId} />
              </span>
              <button
                type="button"
                className="meeting-create-roster__remove"
                aria-label={`Remove ${nameOf(userId)} from roster`}
                onClick={() => removeAttendee(userId)}
                disabled={createMut.running}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No one on the roster yet.</p>
      )}
      <div className="row meeting-create-card__actions">
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            setCreating(false);
            resetCreateForm();
          }}
          disabled={createMut.running}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={createMeeting}
          disabled={createMut.running}
        >
          {createMut.running ? 'Scheduling…' : 'Schedule meeting'}
        </button>
      </div>
    </article>
  );

  return (
    <section className="owha-grid meetings-redesign">
      <PageHeader
        eyebrow="Collaboration"
        title="Meeting Manager"
        subtitle="Track key meetings and convert meeting notes into draft actions under human review."
      />
      <PageActionStrip
        filters={
          <div
            className="meetings-timeframe-filter"
            role="group"
            aria-label="Filter meetings by timeframe"
          >
            {TIMEFRAME_FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`btn sm ${timeframe === option.id ? 'primary' : 'ghost'}`}
                aria-pressed={timeframe === option.id}
                onClick={() => setTimeframe(option.id)}
              >
                {option.label}
              </button>
            ))}
            <span className="chip outline sm">
              {filteredMeetings.length} of {meetings.length} meetings
            </span>
          </div>
        }
        actions={
          <>
            <button
              type="button"
              className="btn primary"
              onClick={() => setCreating(true)}
              disabled={creating}
            >
              Create meeting
            </button>
            <Link className="btn ghost" to="/actions">
              Open Action Center
            </Link>
          </>
        }
      />

      {creating ? (
        createPanel
      ) : meetings.length === 0 ? (
        <BrandedEmptyState
          icon="📋"
          eyebrow="Collaboration"
          title="No meetings on record"
          description="Create or seed meeting records to begin notes capture and human-reviewed action extraction."
          tone="slate"
          action={
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              Schedule the first meeting
            </button>
          }
        />
      ) : (
        <>
          <KpiHeroBand eyebrow="Meeting cadence" items={kpiItems} columns={3} />

          <div className="meetings-overview">
            <article className="card meetings-mix-card">
              <div className="eyebrow">Meeting roster</div>
              <h2 className="card-title">Status mix</h2>
              <StatusDonutMini
                segments={donutSegments}
                size={132}
                totalLabel="Meetings"
                ariaLabel="Meetings by status"
              />
            </article>

            <article className="card meetings-cadence-card">
              <div className="eyebrow">Cadence rollup</div>
              <h2 className="card-title">Portfolio meeting coverage</h2>
              <div>
                <div className="meetings-cadence-row">
                  <span className="meetings-cadence-row__label">Upcoming on the calendar</span>
                  <span className="meetings-cadence-row__metric">
                    <InlineScoreCell
                      value={stats.upcoming}
                      max={Math.max(5, meetings.length)}
                      tone="blue"
                      ariaLabel={`${stats.upcoming} upcoming meetings`}
                    />
                  </span>
                </div>
                <div className="meetings-cadence-row">
                  <span className="meetings-cadence-row__label">Scheduled this week</span>
                  <span className="meetings-cadence-row__metric">
                    <InlineScoreCell
                      value={stats.thisWeek}
                      max={Math.max(5, meetings.length)}
                      tone={stats.thisWeek > 0 ? 'amber' : 'slate'}
                      ariaLabel={`${stats.thisWeek} meetings this week`}
                    />
                  </span>
                </div>
                <div className="meetings-cadence-row">
                  <span className="meetings-cadence-row__label">Held to date</span>
                  <span className="meetings-cadence-row__metric">
                    <InlineScoreCell
                      value={stats.past}
                      max={Math.max(5, meetings.length)}
                      tone="slate"
                      ariaLabel={`${stats.past} meetings held`}
                    />
                  </span>
                </div>
                <div className="meetings-cadence-row">
                  <span className="meetings-cadence-row__label">Governed action items</span>
                  <span className="meetings-cadence-row__metric">
                    <InlineScoreCell
                      value={stats.actionItems}
                      max={Math.max(5, stats.actionItems)}
                      tone={stats.actionItems > 0 ? 'sage' : 'slate'}
                      ariaLabel={`${stats.actionItems} governed action items`}
                    />
                  </span>
                </div>
              </div>
            </article>
          </div>

          {filteredMeetings.length === 0 ? (
            <BrandedEmptyState
              icon="🗓️"
              eyebrow="Timeframe filter"
              title={timeframe === 'upcoming' ? 'Nothing upcoming' : 'Nothing held yet'}
              description="No meetings match the selected timeframe. Switch the filter or schedule a new meeting."
              tone="slate"
            />
          ) : (
            <StaggerReveal as="div" className="action-grid meetings-bento">
              {filteredMeetings.map((meeting) => {
                const tone = meetingTone(meeting, now);
                const attendeeCount = Array.isArray(meeting.attendees)
                  ? meeting.attendees.length
                  : 0;
                const actionCount = Array.isArray(meeting.actionIds) ? meeting.actionIds.length : 0;
                return (
                  <article className={`action-card meeting-card tone-${tone}`} key={meeting.id}>
                    <div className="meeting-card__head">
                      <div className="eyebrow">{meeting.loe || 'OWHA Meeting'}</div>
                    </div>
                    <h2 className="action-card__title meeting-card__title">
                      <Link to={`/meetings/${meeting.id}`}>{meeting.title}</Link>
                    </h2>
                    <div className="meeting-card__scope">
                      <span>
                        {humanizeEnum(meeting.scopeType, 'Scope')}:{' '}
                        <span className="mono">{meeting.scopeId || 'Not linked'}</span>
                      </span>
                      <span className="mono">{formatStart(meeting.startAt)}</span>
                    </div>
                    <div className="meeting-card__metrics">
                      <div className="meeting-card__metric">
                        <span className="meeting-card__metric-label">Attendees</span>
                        <InlineScoreCell
                          value={attendeeCount}
                          max={12}
                          tone="blue"
                          ariaLabel={`${attendeeCount} attendees`}
                        />
                      </div>
                      <div className="meeting-card__metric">
                        <span className="meeting-card__metric-label">Actions</span>
                        <InlineScoreCell
                          value={actionCount}
                          max={Math.max(5, stats.actionItems)}
                          tone={actionCount > 0 ? 'sage' : 'slate'}
                          ariaLabel={`${actionCount} governed actions`}
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
            </StaggerReveal>
          )}
        </>
      )}
    </section>
  );
}

// --- detail page ---------------------------------------------------------

export function MeetingDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { people, nameOf } = usePeople();
  const res = useApi(`/meetings/${id}`, { skip: !id });
  const extractMut = useMutation();
  const convertMut = useMutation();
  const editMut = useMutation();
  const rejectMut = useMutation();
  const [drafts, setDrafts] = useState([]);
  const [confirmExtractOpen, setConfirmExtractOpen] = useState(false);
  const [rejectingDraftId, setRejectingDraftId] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', ownerUserId: '', dueDate: '' });

  usePageMetadata({
    title: 'Meeting Detail',
    subtitle: 'Agenda, notes, decisions, and draft actions extracted under human review.',
  });

  const meeting = res.data?.data;
  const extractedDrafts = Array.isArray(meeting?.extractedDraftActions)
    ? meeting.extractedDraftActions
    : [];
  const extractedDraftsKey = JSON.stringify(
    extractedDrafts.map((draft) => ({
      id: draft.id,
      reviewState: draft.reviewState,
      convertedActionId: draft.convertedActionId || null,
    })),
  );

  useEffect(() => {
    setDrafts((current) => {
      const currentKey = JSON.stringify(
        (Array.isArray(current) ? current : []).map((draft) => ({
          id: draft.id,
          reviewState: draft.reviewState,
          convertedActionId: draft.convertedActionId || null,
        })),
      );
      if (currentKey === extractedDraftsKey) return current;
      return Array.isArray(meeting?.extractedDraftActions) ? meeting.extractedDraftActions : [];
    });
  }, [meeting?.extractedDraftActions, extractedDraftsKey]);

  if (res.loading) return <LoadingSkeleton rows={6} />;
  if (res.error?.status === 404) return <EmptyMeetingState />;
  if (res.error) return <ErrorState error={res.error} onRetry={res.refresh} />;
  if (!meeting) return <EmptyMeetingState />;

  const activeSection = searchParams.get('section') === 'time' ? 'time' : 'overview';
  const pendingDraftCount = drafts.filter((d) => d.reviewState === 'draft').length;

  // Apply a mutated meeting payload (from convert / edit / reject responses)
  // to both the cached envelope and the local draft review list.
  function applyMeetingUpdate(nextMeeting) {
    if (!nextMeeting) return;
    res.setData((previous) => ({
      ...(previous || {}),
      data: nextMeeting,
    }));
    setDrafts(
      Array.isArray(nextMeeting.extractedDraftActions) ? nextMeeting.extractedDraftActions : [],
    );
  }

  async function runExtraction() {
    setConfirmExtractOpen(false);
    try {
      const response = await extractMut.run(() =>
        api.post(`/meetings/${meeting.id}/extract-actions`, {}),
      );
      setDrafts(response.data?.drafts || []);
      if (response.data?.meeting) {
        res.setData((previous) => ({ ...(previous || {}), data: response.data.meeting }));
      }
    } catch (error) {
      toast.error({ title: 'Could not extract draft actions', body: error.message });
    }
  }

  // AC-MTG-2.3: re-running extraction over pending drafts is destructive, so it
  // warns and requires explicit confirmation before replacing them.
  function requestExtraction() {
    if (pendingDraftCount > 0) {
      setConfirmExtractOpen(true);
      return;
    }
    runExtraction();
  }

  async function convertDraftToAction(draftId) {
    try {
      const response = await convertMut.run(() =>
        api.post(`/meetings/${meeting.id}/drafts/${draftId}/convert-to-action`, {}),
      );
      const nextMeeting = response.data?.meeting;
      const action = response.data?.action;
      applyMeetingUpdate(nextMeeting);
      toast.success({
        title: 'Draft converted',
        body: `${action?.title || 'Action'} is now a governed OWHA action.`,
      });
    } catch (error) {
      toast.error({ title: 'Could not convert draft', body: error.message });
    }
  }

  // AC-MTG-2.2: pending drafts stay editable (title / owner / due) until converted.
  function startEditingDraft(draft) {
    setEditingDraftId(draft.id);
    setEditForm({
      title: draft.title || '',
      ownerUserId: draft.ownerUserId || draft.assignedToUserId || '',
      dueDate: draft.dueDate || '',
    });
  }

  async function saveDraftEdits() {
    if (!editingDraftId) return;
    const title = editForm.title.trim();
    if (!title) {
      toast.error({ title: 'Draft title cannot be empty' });
      return;
    }
    try {
      const response = await editMut.run(() =>
        api.patch(`/meetings/${meeting.id}/drafts/${editingDraftId}`, {
          title,
          ownerUserId: editForm.ownerUserId || null,
          dueDate: editForm.dueDate || null,
        }),
      );
      applyMeetingUpdate(response.data?.meeting);
      setEditingDraftId(null);
      toast.success({ title: 'Draft updated', body: 'Edits recorded ahead of conversion.' });
    } catch (error) {
      toast.error({ title: 'Could not save draft edits', body: error.message });
    }
  }

  // AC-MTG-2.2: the human-review "no" path. Rejection locks the draft.
  async function rejectDraft(reason) {
    if (!rejectingDraftId) return;
    try {
      const response = await rejectMut.run(() =>
        api.post(`/meetings/${meeting.id}/drafts/${rejectingDraftId}/reject`, { reason }),
      );
      applyMeetingUpdate(response.data?.meeting);
      setRejectingDraftId(null);
      toast.success({
        title: 'Draft declined',
        body: 'The draft is locked and stays on the record for traceability.',
      });
    } catch (error) {
      toast.error({ title: 'Could not decline draft', body: error.message });
    }
  }

  const attendees = Array.isArray(meeting.attendees) ? meeting.attendees : [];
  const actionCount = Array.isArray(meeting.actionIds) ? meeting.actionIds.length : 0;
  const pendingDrafts = pendingDraftCount;
  const convertedDrafts = drafts.filter(
    (d) => d.reviewState === 'converted' || Boolean(d.convertedActionId),
  ).length;
  const rejectedDrafts = drafts.filter((d) => d.reviewState === 'rejected').length;
  const peopleOptions = people.map((person) => ({ value: person.id, label: person.displayName }));

  // AC-MTG-2.1: evidence anchors — each extracted draft records the exact note
  // line (index + quoted text) that produced it.
  const noteLines = meeting.notes ? String(meeting.notes).split(/\r?\n/) : [];
  const evidenceIndexes = new Set();
  const evidenceTexts = new Set();
  for (const draft of drafts) {
    if (Number.isInteger(draft.sourceNoteIndex)) evidenceIndexes.add(draft.sourceNoteIndex);
    if (draft.sourceNoteLine) evidenceTexts.add(draft.sourceNoteLine);
  }

  const kpiItems = [
    {
      id: 'attendees',
      label: 'Attendees',
      value: attendees.length,
      icon: '👥',
      status: attendees.length > 0 ? 'blue' : 'slate',
      statusLabel: attendees.length > 0 ? 'On the roster' : 'TBD',
    },
    {
      id: 'drafts-pending',
      label: 'Drafts in review',
      value: pendingDrafts,
      icon: '📝',
      status: pendingDrafts > 0 ? 'amber' : 'slate',
      statusLabel: pendingDrafts > 0 ? 'Awaiting human review' : 'Clear',
    },
    {
      id: 'actions-governed',
      label: 'Governed actions',
      value: actionCount || convertedDrafts,
      icon: '✅',
      status: actionCount || convertedDrafts ? 'sage' : 'slate',
      statusLabel: actionCount || convertedDrafts ? 'Active' : 'None yet',
    },
  ];

  const draftSegments = [
    { label: 'In review', count: pendingDrafts, tone: 'amber' },
    { label: 'Converted', count: convertedDrafts, tone: 'sage' },
    { label: 'Declined', count: rejectedDrafts, tone: 'slate' },
  ];

  const rejectingDraft = drafts.find((d) => d.id === rejectingDraftId) || null;

  return (
    <section className="owha-grid meetings-redesign">
      <PageHeader
        eyebrow={meeting.loe || 'Meeting'}
        title={meeting.title}
        subtitle={`${humanizeEnum(meeting.scopeType, 'Scope')}: ${meeting.scopeId || 'Not linked'} · ${meeting.startAt?.slice(0, 16) || 'TBD'}`}
        actions={
          <div className="row" style={{ gap: 8 }}>
            {activeSection === 'time' ? (
              <Link className="btn ghost" to={`/meetings/${meeting.id}`}>
                Back to overview
              </Link>
            ) : null}
            <Link
              className="btn ghost"
              to={`/meetings/${meeting.id}?section=time`}
              aria-current={activeSection === 'time' ? 'true' : undefined}
            >
              Convert to time
            </Link>
            <button
              className="btn primary"
              type="button"
              onClick={requestExtraction}
              disabled={extractMut.running}
            >
              Extract draft actions
            </button>
          </div>
        }
      />

      <KpiHeroBand eyebrow="Meeting snapshot" items={kpiItems} columns={3} />

      {activeSection === 'time' ? (
        // AC-MTG-2.4: the attendance-to-time conversion lives on the detail page
        // as an embedded section (?section=time) sharing the FS-TIME-5 panel.
        <MeetingTimeConversionPanel meeting={meeting} />
      ) : (
        <div className="meeting-detail-layout">
          <article className="card">
            <div className="eyebrow">Meeting notes</div>
            <h2 className="card-title">Notes and decisions</h2>
            {noteLines.length ? (
              <ol className="meeting-notes meeting-notes-lines">
                {noteLines.map((line, index) => {
                  const isEvidence =
                    evidenceIndexes.has(index) || (Boolean(line) && evidenceTexts.has(line));
                  return (
                    <li
                      key={`${index}-${line}`}
                      data-testid={`meeting-note-line-${index}`}
                      data-evidence={isEvidence ? 'true' : 'false'}
                      className={`meeting-note-line${isEvidence ? ' is-evidence' : ''}`}
                    >
                      <span className="meeting-note-line__num mono" aria-hidden="true">
                        {index + 1}
                      </span>
                      <span className="meeting-note-line__text">{line || ' '}</span>
                      {isEvidence ? (
                        <span className="meeting-note-line__badge">Draft source</span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="muted">No notes recorded.</p>
            )}
          </article>

          <aside className="meeting-detail-aside">
            <article className="card">
              <div className="eyebrow">Attendees</div>
              <h2 className="card-title">Participants</h2>
              {attendees.length ? (
                <ul className="meeting-roster">
                  {attendees.map((attendee) => (
                    <li key={attendee}>
                      <span className="meeting-roster__avatar" aria-hidden="true">
                        {initialsFor(nameOf(attendee))}
                      </span>
                      <span className="meeting-roster__name">
                        <PersonLabel userId={attendee} />
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No attendees recorded.</p>
              )}
            </article>

            <article className="card">
              <div className="meetings-section-head">
                <div>
                  <div className="eyebrow">Draft action extraction</div>
                  <h2 className="card-title">Human review required</h2>
                </div>
                {drafts.length ? (
                  <StatusDonutMini
                    segments={draftSegments}
                    size={84}
                    totalLabel="Drafts"
                    ariaLabel="Draft actions by review state"
                  />
                ) : null}
              </div>
              {drafts.length ? (
                <ul className="meeting-draft-list">
                  {drafts.map((draft) => {
                    const pending = draft.reviewState === 'draft';
                    const rejected = draft.reviewState === 'rejected';
                    const tone = pending ? 'amber' : rejected ? 'slate' : 'sage';
                    const editing = editingDraftId === draft.id;
                    return (
                      <li key={draft.id} className={`meeting-draft-item tone-${tone}`}>
                        <div className="meeting-draft-item__body">
                          <strong className="meeting-draft-item__title">{draft.title}</strong>
                          <div className="meeting-draft-item__meta">
                            <PersonLabel
                              userId={draft.ownerUserId || draft.assignedToUserId}
                              fallback="Owner pending"
                            />{' '}
                            · {humanizeEnum(draft.reviewState)}
                            {draft.dueDate ? (
                              <>
                                {' '}
                                · due <span className="mono">{draft.dueDate}</span>
                              </>
                            ) : null}
                          </div>
                          {draft.sourceNoteLine ? (
                            <div className="meeting-draft-item__evidence">
                              <span className="meeting-draft-item__evidence-label">Evidence</span>{' '}
                              &ldquo;{draft.sourceNoteLine}&rdquo;
                            </div>
                          ) : null}
                          {draft.convertedActionId ? (
                            <div className="meeting-draft-item__converted">
                              Converted to {draft.convertedActionId}
                            </div>
                          ) : null}
                          {rejected ? (
                            <div className="meeting-draft-item__rejection">
                              Locked after human review
                              {draft.rejectionReason ? ` — ${draft.rejectionReason}` : ''}
                            </div>
                          ) : null}
                          {editing ? (
                            <div className="meeting-draft-edit">
                              <FormField label="Draft title" htmlFor={`draft-title-${draft.id}`}>
                                <TextInput
                                  id={`draft-title-${draft.id}`}
                                  value={editForm.title}
                                  onChange={(value) =>
                                    setEditForm((form) => ({ ...form, title: value }))
                                  }
                                  disabled={editMut.running}
                                />
                              </FormField>
                              <FormField label="Owner" htmlFor={`draft-owner-${draft.id}`}>
                                <Select
                                  id={`draft-owner-${draft.id}`}
                                  value={editForm.ownerUserId}
                                  onChange={(value) =>
                                    setEditForm((form) => ({ ...form, ownerUserId: value }))
                                  }
                                  placeholder="Select an owner…"
                                  options={peopleOptions}
                                  disabled={editMut.running}
                                />
                              </FormField>
                              <FormField label="Due date" htmlFor={`draft-due-${draft.id}`}>
                                <DateInput
                                  id={`draft-due-${draft.id}`}
                                  value={editForm.dueDate}
                                  onChange={(value) =>
                                    setEditForm((form) => ({ ...form, dueDate: value }))
                                  }
                                  disabled={editMut.running}
                                />
                              </FormField>
                              <div className="meeting-draft-edit__actions">
                                <button
                                  type="button"
                                  className="btn ghost sm"
                                  onClick={() => setEditingDraftId(null)}
                                  disabled={editMut.running}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn primary sm"
                                  onClick={saveDraftEdits}
                                  disabled={editMut.running}
                                >
                                  Save draft
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        {pending && !editing ? (
                          <div className="meeting-draft-item__actions">
                            <button
                              className="btn outline sm"
                              type="button"
                              onClick={() => convertDraftToAction(draft.id)}
                              disabled={convertMut.running}
                            >
                              Convert to live action
                            </button>
                            <button
                              className="btn ghost sm"
                              type="button"
                              onClick={() => startEditingDraft(draft)}
                            >
                              Edit draft
                            </button>
                            <button
                              className="btn ghost sm"
                              type="button"
                              onClick={() => setRejectingDraftId(draft.id)}
                            >
                              Reject draft
                            </button>
                          </div>
                        ) : null}
                        {draft.convertedActionId ? (
                          <Link className="btn ghost sm" to={`/actions/${draft.convertedActionId}`}>
                            Open action
                          </Link>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <BrandedEmptyState
                  icon="📝"
                  eyebrow="Draft extraction"
                  title="No draft actions extracted yet"
                  description="Run extraction to surface candidate actions from the meeting notes for human review."
                  tone="slate"
                />
              )}
            </article>
          </aside>
        </div>
      )}

      <ConfirmActionDialog
        open={confirmExtractOpen}
        title="Replace pending drafts?"
        body={`Re-running extraction will replace ${pendingDraftCount} pending draft${
          pendingDraftCount === 1 ? '' : 's'
        } awaiting human review with a fresh set from the meeting notes. Converted and declined drafts are unaffected.`}
        confirmLabel="Replace drafts"
        onConfirm={runExtraction}
        onCancel={() => setConfirmExtractOpen(false)}
        busy={extractMut.running}
      />

      <ConfirmActionDialog
        open={Boolean(rejectingDraftId)}
        tone="danger"
        title="Reject this draft?"
        body={`Declining "${rejectingDraft?.title || 'this draft'}" locks it — it can no longer be edited or converted. It stays on the record for traceability.`}
        confirmLabel="Reject draft"
        showNote
        noteHint="Optional — recorded with the review decision."
        notePlaceholder="Why is this draft not becoming an action?"
        onConfirm={(note) => rejectDraft(note)}
        onCancel={() => setRejectingDraftId(null)}
        busy={rejectMut.running}
      />
    </section>
  );
}

function EmptyMeetingState() {
  return (
    <section className="owha-grid meetings-redesign">
      <BrandedEmptyState
        icon="🔍"
        eyebrow="Meeting Manager"
        title="Meeting not found"
        description="This meeting record is unavailable or may have been removed."
        tone="slate"
        action={
          <Link className="btn ghost" to="/meetings">
            Back to Meeting Manager
          </Link>
        }
      />
    </section>
  );
}
